// Веб-пуш уведомления о новых объявлениях (barter-026, по образцу Айлы
// app/src/lib/pushsub.js): подписка из PWA, критерии дайджеста — активные
// подписки пользователя (категории/города в «Додатково»). Сервер хранит
// подписки в D1 и после sync-репаблиша присылает дайджест не чаще раза
// в сутки. Работает в обоих режимах (веб и Telegram), где браузер
// поддерживает PushManager (в webview Telegram может отсутствовать —
// Settings показывает честную диагностику).

import { API_BASE } from './api-base';
import { authHeaders } from './auth';
import { getActiveSubscriptions } from '../db/database';
import type { PushCriteria } from '@barter/shared';

const LS_WANTED = 'barter.pushWanted'; // '1' — пользователь включал уведомления

export function pushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

/** «Пользователь хочет пуши» — переживает перезаходы (самолечение, aila-035). */
export function pushWanted(): boolean {
  try {
    return localStorage.getItem(LS_WANTED) === '1';
  } catch {
    return false;
  }
}
function setWanted(v: boolean) {
  try {
    if (v) localStorage.setItem(LS_WANTED, '1');
    else localStorage.removeItem(LS_WANTED);
  } catch {
    /* приватный режим */
  }
}

export interface PushState {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
}

export async function currentPushState(): Promise<PushState> {
  if (!pushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, permission: Notification.permission, subscribed: !!sub };
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()), ...(init?.headers || {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

/** Критерии дайджеста — активные подписки пользователя (до 10, как в Settings). */
async function digestCriteria(): Promise<PushCriteria[]> {
  const subs = await getActiveSubscriptions();
  return subs.slice(0, 10).map((s) => ({
    categoryId: s.categoryId,
    city: s.city,
    keywords: s.keywords,
  }));
}

/** Диагностика для карточки в Settings: знает ли сервер эту подписку и чья она. */
export async function pushServerStatus(endpoint: string): Promise<{ known: boolean; mine: boolean | null }> {
  return api(`/push/status?endpoint=${encodeURIComponent(endpoint)}`);
}

async function subscribeAndRegister(): Promise<void> {
  const { key } = await api<{ key: string }>('/push/key');
  if (!key) throw new Error('Push is not configured on server');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // TS-типы DOM требуют ArrayBuffer-подложку — приводим к BufferSource
      applicationServerKey: urlB64ToUint8(key) as unknown as BufferSource,
    });
  }
  const j = sub.toJSON();
  if (!j.endpoint || !j.keys) throw new Error('Push subscription failed');
  // перезапостим всегда: сервер мог потерять строку или подписка досталась
  // другому аккаунту (переустановка) — критерии заодно обновляются
  await api('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys, criteria: await digestCriteria() }),
  });
}

export type EnablePushResult = { ok: true } | { ok: false; reason: 'permission' | 'error' };

export async function enablePush(): Promise<EnablePushResult> {
  if (!pushSupported()) return { ok: false, reason: 'error' };
  try {
    if (Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        // намерение помним даже при отказе: пока denied — подсказка «как починить»
        setWanted(true);
        return { ok: false, reason: 'permission' };
      }
    }
    await subscribeAndRegister();
    setWanted(true);
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

export async function disablePush(): Promise<void> {
  setWanted(false);
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      try {
        await api('/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
      } catch {
        /* сервер мог уже забыть */
      }
      await sub.unsubscribe();
    }
  } catch {
    /* SW не поднялся — нечего отписывать */
  }
}

/** Тихая (пере)подписка для уже выданного разрешения: чинит потерянные на
 *  сервере строки и ротацию FCM (pushsubscriptionchange в Chrome почти не
 *  срабатывает). Срабатывает только когда пользователь включал уведомления —
 *  сознательно выключенные самолечением не возвращаются. */
const ENSURE_THROTTLE_MS = 60_000;
let lastEnsureAt = 0;
export async function ensurePush(): Promise<void> {
  if (!pushSupported() || typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted' || !pushWanted()) return;
  if (Date.now() - lastEnsureAt < ENSURE_THROTTLE_MS) return;
  lastEnsureAt = Date.now();
  try {
    await subscribeAndRegister();
  } catch {
    /* сеть/сервер — попробуем при следующем заходе */
  }
}

/** Самолечение на возврате в приложение: пока вкладка спала, подписка
 *  могла протухнуть — тихо переподписываем. Вызывается один раз из main.tsx. */
export function initPushSelfHeal(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void ensurePush();
  });
}

/** Тестовое уведомление себе (кнопка «Перевірити» в настройках). */
export async function sendTestPush(): Promise<{ total: number; sent: number }> {
  return api('/push/test', { method: 'POST' });
}

function urlB64ToUint8(b64: string): Uint8Array {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}
